import { type FC, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { usePdpSharedTracking } from '../../hooks/usePdpSharedTracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { SOLD_PDP_V3, PDP_SPACING_OPTIMIZATION } from '../../../constants/abTestsConstants';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { getQueryParam } from '../../../utils/uriUtils';
import scrollIntoView from 'scroll-into-view';
import { BUYER_INCENTIVES } from '../../helpers/tabConstants';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { PdpRespUnavailableHeader } from '../PdpRespUnavailableHeader/PdpRespUnavailableHeader';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { PdpRespUnavailableHeaderYMAL } from '../PdpRespUnavailableHeaderYMAL/PdpRespUnavailableHeaderYMAL';
import { PdpSharedAuctionEndedLink } from '../../shared/PdpSharedAuctionEndedLink/PdpSharedAuctionEndedLink';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs';
import { youMayAlsoLike } from '../../../shared/sharedMessages';
import {
    PdpRespUnavailableDetailsTabs,
    useUnavailableDetailTabsState,
} from '../PdpRespUnavailableDetailsTabs/PdpRespUnavailableDetailsTabs';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';
import { PdpRespSharedSellerChatLoader } from '../../respShared/PdpRespSharedSellerChatLoader/PdpRespSharedSellerChatLoader';
import { PdpRespSharedPrintDetailsLazy } from '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy';
import { BrowserHistoryContextProvider } from '../../../global/BrowserHistoryContext/BrowserHistoryContext';
import { PdpSharedYMALItemsCarousel } from '../../shared/PdpSharedYMALItemsCarousel/PdpSharedYMALItemsCarousel';
import { PdpSharedSellerItemsCarousel } from '../../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel';
import { PdpRespSimilarItemsGrid } from '../../PdpRespSimilarItems/PdpRespSimilarItemsGrid';

// styles
import styles from './PdpRespUnavailableContainer.scss';
import dibsCss from 'dibs-css';

// types
import { type PdpRespUnavailableContainer_item$key as Item } from './__generated__/PdpRespUnavailableContainer_item.graphql';
import { type PdpRespUnavailableContainer_user$key as User } from './__generated__/PdpRespUnavailableContainer_user.graphql';
import { type PdpRespUnavailableContainer_viewer$key as Viewer } from './__generated__/PdpRespUnavailableContainer_viewer.graphql';

// lazy components
const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites'
        )
);

const PdpRespSharedHeroCarouselPrint = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedHeroCarouselPrint/PdpRespSharedHeroCarouselPrint'
        )
);

const PdpSharedOptInPromptModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../PdpSharedOptInPromptModal/PdpSharedOptInPromptModal'
        )
);

const PdpRespSharedSidebarSellerInfoAndContact = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedSidebarSellerInfoAndContact" */ '../../PdpRespSidebarSellerInfo/PdpRespSharedSidebarSellerInfoAndContact'
        )
);

const PdpSharedUnavailableViewPrice = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedUnavailableViewPrice" */ '../../PdpSharedUnavailableViewPrice/PdpSharedUnavailableViewPrice'
        )
);

const ViewPriceToast = dibsLazyClient(
    () =>
        import(/* webpackChunkName: "ViewPriceToast" */ 'dibs-buyer-layout/exports/ViewPriceToast')
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableContainer_viewer on Viewer {
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        ...usePdpSharedTracking_viewer
        ...SharedRespLinksBlock_viewer
        ...PdpRespUnavailableHeaderYMAL_viewer
        ...PdpRespUnavailableDetailsTabs_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespSharedSellerChatLoader_viewer @include(if: $fetchChat)
        ...PdpSharedSellerItemsCarousel_viewer
        ...PdpSharedYMALItemsCarousel_viewer @skip(if: $isSoldPDPV3Variant)
        similarItemsGrid: moreLikeThisItem(
            first: 40
            seller: EXCLUDE
            itemId: $itemId
            useML: $isIncorporateMLYmalVariant
        ) @include(if: $isSoldPDPV3Variant) {
            edges {
                node {
                    serviceId
                    ...PdpRespSimilarItemsGrid_similarItems
                }
            }
        }
        ...PdpRespSimilarItemsGrid_viewer @include(if: $isSoldPDPV3Variant)
    }
`;
const itemFragment = graphql`
    fragment PdpRespUnavailableContainer_item on Item {
        serviceId
        isAuctionEnabled
        seller {
            serviceId
            isAccessible
            ...PdpRespSharedSidebarSellerInfoAndContact_seller @include(if: $afterDisplay)
        }
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        ...PdpRespSharedTitle_item
        ...usePdpSharedTracking_item
        ...SharedRespLinksBlock_item
        ...PdpRespUnavailableHeader_item
        ...PdpRespSharedBreadcrumbs_item
        ...PdpRespUnavailableDetailsTabs_item
        ...PdpRespSharedHeroCarousel_item
        ...PdpSharedEditorialArticles_refactor_item
        ...PdpRespSharedPrintDetailsLazy_item
        ...PdpSharedAuctionClosed_item @include(if: $afterDisplay)
        ...PriceDisplay_item
            @include(if: $afterDisplayOrBuaSsr)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
        ...PdpRespSharedHeroCarouselPrint_item @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_item @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_item
        ...PdpSharedUnavailableViewPrice_item
        ...PdpRespSimilarItemsGrid_item @include(if: $isSoldPDPV3Variant)
    }
`;
const userFragment = graphql`
    fragment PdpRespUnavailableContainer_user on User {
        serviceId
        ...PdpRespUnavailableHeader_user
        ...PdpRespUnavailableDetailsTabs_user
        ...PdpRespSharedHeroCarousel_user
        ...PdpRespSharedPrintDetailsLazy_user
        ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_user @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_user
        ...PdpSharedYMALItemsCarousel_user @skip(if: $isSoldPDPV3Variant)
        ...PdpRespSimilarItemsGrid_user @include(if: $isSoldPDPV3Variant)
        ...usePdpSharedTracking_user
    }
`;

export const PdpRespUnavailableContainer: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isBot, isSoldPDPV3Variant, isPdpSpacingOptimizationVariant } = useServerVarsContext();

    const {
        currency,
        relayVars: { afterDisplay, fetchChat },
    } = useRespUnavailableContext();
    const [showOptinPrompt, setShowOptinPrompt] = useState(false);
    const [viewPriceToast, setViewPriceToast] = useState(false);
    const detailTabsState = useUnavailableDetailTabsState();

    usePdpSharedTracking({ item, viewer, user });

    const { serviceId: userId } = user || {};
    const { isWordpressArticlesEnabled, similarItemsGrid } = viewer;
    const { seller, serviceId: itemId, internalBuyLinks, isAuctionEnabled } = item;

    useEffect(() => {
        if (!isAuctionEnabled) {
            trackAbTestV2Variant(SOLD_PDP_V3);
        }
    }, [isAuctionEnabled]);

    useDwelledOnPage(PAGE_TYPE.PDP);

    useEffect(() => {
        trackAbTestV2Variant(PDP_SPACING_OPTIMIZATION);
    }, []);

    const similarItemsRef = useRef(null);

    const scrollToBuyerIncentives = useRef(true);
    const detailsSectionRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const displayBuyerIncentivesParam = getQueryParam(
            location.search,
            'displayBuyerIncentives'
        );
        const shouldScrollToBuyerIncentivesDetails =
            scrollToBuyerIncentives.current && displayBuyerIncentivesParam === 'true';
        if (shouldScrollToBuyerIncentivesDetails) {
            const detailsSection = detailsSectionRef?.current;
            if (detailsSection) {
                const [, setActiveTab] = detailTabsState;
                setActiveTab(BUYER_INCENTIVES);
                scrollToBuyerIncentives.current = false;
                scrollIntoView(detailsSection, { time: 500 }, () => detailsSection.focus());
            }
        }
    }, [detailTabsState]);

    const priceDisplay = (
        <PriceDisplay
            sku={null}
            useSvgIcon
            item={item}
            displayVatLabel
            currency={currency}
            hasPriceBlur={!userId}
            showSoldListPrice={false}
        />
    );

    return (
        <>
            <div className={styles.printHide}>
                {!isSoldPDPV3Variant && (
                    <div className={styles.topModuleContainer}>
                        <div className={styles.topModuleContainerInner}>
                            <PdpRespUnavailableHeader
                                item={item}
                                user={user}
                                setViewPriceToast={setViewPriceToast}
                                isPdpSpacingOptimizationVariant={!!isPdpSpacingOptimizationVariant}
                            />
                            <div className={styles.divider} />
                            <PdpRespUnavailableHeaderYMAL
                                viewer={viewer}
                                isPdpSpacingOptimizationVariant={!!isPdpSpacingOptimizationVariant}
                            />
                        </div>
                    </div>
                )}

                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={`${styles.topContainer} ${styles.carouselContainer} colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1`}
                >
                    <div className={styles.printHide}>
                        <BrowserHistoryContextProvider>
                            <PdpRespSharedHeroCarousel
                                viewer={viewer}
                                item={item}
                                user={user}
                                currency={currency}
                                afterDisplay={afterDisplay}
                                size={isSoldPDPV3Variant ? 'xxSmall' : 'small'}
                            />
                        </BrowserHistoryContextProvider>
                    </div>
                    <ClientSuspense fallback={null}>
                        <div className={styles.printShow}>
                            <PdpRespSharedHeroCarouselPrint item={item} />
                        </div>
                    </ClientSuspense>
                </div>
                <div className={`${styles.topContainer} colXs12 colLg4 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        {isAuctionEnabled && <PdpSharedAuctionEndedLink />}
                        <div className="rowFlex">
                            <div className="colXs12 colLg10">
                                <PdpRespSharedTitle
                                    item={item}
                                    isSoldPDPV3Variant={isSoldPDPV3Variant}
                                />
                                <div className={styles.printHide}>
                                    {isBot ? (
                                        priceDisplay
                                    ) : (
                                        <ClientSuspense fallback={null}>
                                            {priceDisplay}
                                        </ClientSuspense>
                                    )}
                                </div>

                                {!isSoldPDPV3Variant && (
                                    <ClientSuspense fallback="">
                                        {!userId && (
                                            <PdpSharedUnavailableViewPrice
                                                item={item}
                                                currency={currency}
                                                launchOrigin="sold gating pdp sidebar"
                                                setViewPriceToast={setViewPriceToast}
                                            />
                                        )}
                                    </ClientSuspense>
                                )}
                            </div>
                            <div
                                className={`${styles.printHide} ${styles.favoritesContainer} colXs2`}
                            >
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                    />
                                </ClientSuspense>
                            </div>
                        </div>
                        {isSoldPDPV3Variant && (
                            <ClientSuspense fallback="">
                                {!userId && (
                                    <PdpSharedUnavailableViewPrice
                                        item={item}
                                        currency={currency}
                                        launchOrigin="sold gating pdp sidebar"
                                        setViewPriceToast={setViewPriceToast}
                                        isSoldPDPV3Variant={isSoldPDPV3Variant}
                                    />
                                )}
                            </ClientSuspense>
                        )}
                        {seller?.isAccessible && (
                            <ClientSuspense fallback={null}>
                                <div
                                    className={classnames(
                                        {
                                            [dibsCss.mtXsmall]: isSoldPDPV3Variant,
                                            [dibsCss.mbXsmall]: isSoldPDPV3Variant,
                                            [styles.topDivider]: isSoldPDPV3Variant,
                                            [styles.sidebarSection]: !isSoldPDPV3Variant,
                                        },
                                        styles.printHide
                                    )}
                                >
                                    <PdpRespSharedSidebarSellerInfoAndContact
                                        template="UNAVAILABLE"
                                        user={user}
                                        item={item}
                                        seller={item.seller}
                                        currency={currency}
                                        isSoldPDPV3Variant={isSoldPDPV3Variant}
                                        isClient={afterDisplay}
                                    />
                                </div>
                            </ClientSuspense>
                        )}
                        <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                    </div>
                </div>
            </div>

            <div ref={detailsSectionRef} className={styles.printHide}>
                <PdpRespUnavailableDetailsTabs
                    user={user}
                    item={item}
                    viewer={viewer}
                    tabsState={detailTabsState}
                />
            </div>
            <div className={styles.printShow}>
                <PdpRespSharedPrintDetailsLazy item={item} user={user} currency={currency} />
            </div>

            {fetchChat && <PdpRespSharedSellerChatLoader viewer={viewer} currency={currency} />}

            <div className={styles.printHide}>
                {isSoldPDPV3Variant && (
                    <div ref={similarItemsRef}>
                        <PdpRespSimilarItemsGrid
                            isSoldPDPV3Variant={isSoldPDPV3Variant}
                            header={youMayAlsoLike}
                            relayVariables={{ isClient: afterDisplay }}
                            viewer={viewer}
                            currency={currency}
                            user={user}
                            item={item}
                            similarItems={flattenConnectionNodes(similarItemsGrid)}
                            favoritesLocation={'SIMILAR_ITEM_GRID'}
                            gridLocation="SIMILAR_ITEM_GRID"
                            dataTn="pdp-similar-recommendation-grid"
                            isDark={false}
                            showLightBox={false}
                            hideBar
                            gridItemWrapperClass={styles.similarItem}
                        />
                    </div>
                )}
                <PdpSharedSellerItemsCarousel
                    viewer={viewer}
                    item={item}
                    user={user}
                    currency={currency}
                />

                {!isSoldPDPV3Variant && (
                    <PdpSharedYMALItemsCarousel viewer={viewer} user={user} currency={currency} />
                )}

                <RecentlyViewedItemsProvider excludedItemId={item.serviceId}>
                    <RecentlyViewedItemsLazy
                        itemsPerPage={5}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        wrapperClassName={classnames(styles.printHide, styles.recentlyViewed, {
                            [styles.divider]: viewer.isWordpressArticlesEnabled,
                        })}
                    />
                </RecentlyViewedItemsProvider>
            </div>

            {isWordpressArticlesEnabled && (
                <div className={styles.printHide}>
                    <PdpSharedEditorialArticles item={item} />
                </div>
            )}

            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>

            {showOptinPrompt && (
                <ClientSuspense fallback="">
                    <PdpSharedOptInPromptModal
                        isAuctionEnabled={false}
                        onModalClose={() => setShowOptinPrompt(false)}
                    />
                </ClientSuspense>
            )}

            {viewPriceToast && (
                <ClientSuspense fallback="">
                    <ViewPriceToast
                        isOpen={viewPriceToast}
                        onClose={() => setViewPriceToast(false)}
                    />
                </ClientSuspense>
            )}
        </>
    );
};
